theme.headerSearch = (function () {
  function settleOpeningFocus(panel, opener, activeClass, chooseTarget) {
    let cancelled = false;
    const focusDeadline = Date.now() + 2000;
    let firstFrame;
    let secondFrame;
    let timer;
    const cancel = () => {
      if (cancelled) return;
      cancelled = true;
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      clearTimeout(timer);
      panel.removeEventListener('transitionend', onTransition);
      panel.removeEventListener('transitioncancel', onTransition);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerdown', cancel, true);
      document.removeEventListener('keydown', onInteraction, true);
    };
    const canMoveFocus = () => document.activeElement === opener || document.activeElement === document.body || document.activeElement === document.documentElement;
    const attempt = () => {
      if (cancelled) return;
      if (Date.now() > focusDeadline) { cancel(); return; }
      if (!panel.isConnected || panel.inert || !panel.classList.contains(activeClass) || panel.contains(document.activeElement) || !canMoveFocus()) { cancel(); return; }
      const target = chooseTarget();
      if (!target?.getClientRects().length || ['hidden', 'collapse'].includes(getComputedStyle(target).visibility)) return;
      if (typeof document.elementFromPoint === 'function') {
        const rect = target.getBoundingClientRect();
        const topElement = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
        if (!topElement || !target.contains(topElement)) return;
      }
      target.focus({ preventScroll: true });
      if (panel.contains(document.activeElement)) cancel();
    };
    const onTransition = (event) => { if (event.target === panel) attempt(); };
    const onFocusIn = () => { if (panel.contains(document.activeElement) || !canMoveFocus()) cancel(); };
    const onInteraction = (event) => { if (!['Tab', 'Escape'].includes(event.key)) cancel(); };
    panel.addEventListener('transitionend', onTransition);
    panel.addEventListener('transitioncancel', onTransition);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', cancel, true);
    document.addEventListener('keydown', onInteraction, true);
    panel.getBoundingClientRect();
    const style = getComputedStyle(panel);
    const milliseconds = (value) => (Number.parseFloat(value) || 0) * (value.trim().endsWith('ms') ? 1 : 1000);
    const durations = (style.transitionDuration || '0s').split(',').map(milliseconds);
    const delays = (style.transitionDelay || '0s').split(',').map(milliseconds);
    const duration = Math.max(0, ...durations.map((value, index) => value + delays[index % delays.length]));
    const retry = () => {
      attempt();
      if (cancelled) return;
      const remaining = focusDeadline - Date.now();
      if (remaining <= 0) { cancel(); return; }
      timer = setTimeout(retry, Math.min(50, remaining));
    };
    timer = setTimeout(retry, Math.min(duration + 80, 2000));
    firstFrame = requestAnimationFrame(() => {
      attempt();
      if (!cancelled) secondFrame = requestAnimationFrame(attempt);
    });
    return cancel;
  }
  function searchOverlay() {
    const header = document.querySelector('.header__area');
    if (header) document.documentElement.style.setProperty('--header-height', `${header.clientHeight}px`);
    const cleanups = [];
    const visible = (element) => element && !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      element.getClientRects().length > 0 && !['hidden', 'collapse'].includes(getComputedStyle(element).visibility);

    function quickDrawer(triggerSelector, closeId, panelSelector) {
      const panel = document.querySelector(panelSelector);
      const triggers = Array.from(document.querySelectorAll(triggerSelector));
      const closeButton = document.getElementById(closeId);
      if (!panel || !closeButton || !triggers.length || panel.dataset.irDrawerBound) return;
      panel.dataset.irDrawerBound = 'true';
      const events = new AbortController();
      const options = { signal: events.signal };
      let opener;
      let previousOverflow;
      let background = [];
      let cancelPendingFocus;
      const focusable = () => Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]'))
        .filter((element) => element.tabIndex >= 0 && visible(element));
      const focusTarget = () => {
        const input = panel.querySelector('input[type="search"]');
        return visible(input) ? input : focusable()[0] || panel;
      };
      const close = (restoreFocus = true) => {
        cancelPendingFocus?.();
        cancelPendingFocus = null;
        if (!panel.classList.contains('active')) return;
        panel.classList.remove('active');
        panel.removeAttribute('aria-modal');
        background.forEach(([element, wasInert]) => { element.inert = wasInert; });
        background = [];
        document.body.style.overflowY = previousOverflow;
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        if (restoreFocus) {
          const returnTarget = visible(opener) ? opener : triggers.find(visible) || document.querySelector('.header__logo_link');
          if (visible(returnTarget)) returnTarget.focus();
        }
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
      };
      const open = (trigger) => {
        if (panel.classList.contains('active')) return;
        opener = trigger;
        previousOverflow = document.body.style.overflowY;
        panel.inert = false;
        panel.removeAttribute('aria-hidden');
        panel.classList.add('active');
        panel.setAttribute('aria-modal', 'true');
        document.body.style.overflowY = 'hidden';
        triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
        let branch = panel;
        while (branch.parentElement && branch !== document.body) {
          Array.from(branch.parentElement.children).forEach((sibling) => {
            if (sibling !== branch && !sibling.classList.contains('offcanvas-overlay') && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
              background.push([sibling, sibling.inert]);
              sibling.inert = true;
            }
          });
          branch = branch.parentElement;
        }
        cancelPendingFocus = settleOpeningFocus(panel, opener, 'active', focusTarget);
      };
      panel.setAttribute('role', 'dialog');
      const label = panel.querySelector('label[for]');
      if (!panel.hasAttribute('aria-label') && !panel.hasAttribute('aria-labelledby')) {
        panel.setAttribute('aria-label', label?.textContent.trim() || triggers[0].getAttribute('aria-label') || closeButton.textContent.trim());
      }
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => { event.preventDefault(); open(trigger); }, options);
      });
      closeButton.addEventListener('click', (event) => { event.preventDefault(); close(); }, options);
      document.addEventListener('click', (event) => {
        if (!panel.contains(event.target) && !triggers.some((trigger) => trigger.contains(event.target))) close();
      }, options);
      document.addEventListener('keydown', (event) => {
        if (!panel.classList.contains('active')) return;
        if (!panel.contains(document.activeElement) && document.activeElement !== opener && document.activeElement !== document.body && document.activeElement !== document.documentElement) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close();
        } else if (event.key === 'Tab') {
          const elements = focusable();
          const first = elements[0] || panel;
          const last = elements[elements.length - 1] || panel;
          if (!elements.length || !panel.contains(document.activeElement) || (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) || (!event.shiftKey && document.activeElement === last)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
          }
        }
      }, options);
      cleanups.push(() => { close(false); events.abort(); delete panel.dataset.irDrawerBound; });
    }
    quickDrawer('.header__actions_btn--search', 'search__close_btn', '#predictive__search_overlay');
    quickDrawer('.information--drawer__trigger', 'quick__information--close_btn', '#quick__information_overlay');
    this.onUnload = () => cleanups.forEach((cleanup) => cleanup());
  }
  return searchOverlay;
})();
